import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {PushNotificationFn} from 'app/components/Notification';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {
  ColumnSettingsContent,
  OrderedVisibleColumnConfiguration,
} from 'app/pages/PropertyOverview/PropertyTableColumnSettings';
import {getDefaultPropertyOverviewColumnSettings} from 'app/pages/PropertyOverview/utils';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {useStateWithDeps} from 'app/utils/hookUtils';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import cs from './OrganizationView.module.scss';

export const OrganizationDefaultPropertyOverviewColumns: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    setDefaultPropertyOverviewColumns: LoggedInUserActions['setOrganizationPropertyOverviewColumnSettings'];
    pushNotification: PushNotificationFn;
  }>
> = ({
  organization,
  profile,
  setDefaultPropertyOverviewColumns: saveDefaultColumns,
  pushNotification,
}) => {
  const [columnSettings, setColumnSettings] = useStateWithDeps<OrderedVisibleColumnConfiguration>(
    getDefaultPropertyOverviewColumnSettings(organization),
    [organization]
  );

  const [isSaving, setIsSaving] = React.useState(false);
  const canUpdate = isOwnerOrAdmin(profile.get('role'));

  const handleSave = React.useCallback(async () => {
    try {
      setIsSaving(true);
      await saveDefaultColumns(organization.get('id'), columnSettings);
      pushNotification({
        message: 'Saved default property overview columns.',
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.SUCCESS,
        },
      });
    } catch (e) {
      console.error(e);
      pushNotification({
        message: 'Error saving default columns. Please try again.',
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } finally {
      setIsSaving(false);
    }
  }, [organization, columnSettings, saveDefaultColumns, pushNotification]);

  return (
    <div className={cs.field}>
      <label>Default property overview columns</label>
      <p>
        Set the default columns and their order for the property overview table for all portfolios.
      </p>
      <div className={cs.propertyOverviewColumnsContainer}>
        <ColumnSettingsContent
          additionalFeatureProperties={I.Set()}
          columnSettings={columnSettings}
          onColumnSettingsChange={setColumnSettings}
          saveColumnSettings={async (orgId, settings) => {
            await saveDefaultColumns(orgId, settings);
            setColumnSettings(settings);
          }}
          hideMenuItems={true}
        />
      </div>
      <div style={{marginTop: '1rem'}}>
        <B.Tooltip
          content={'Only organization admins can update this setting.'}
          disabled={canUpdate}
        >
          <B.Button
            onClick={handleSave}
            disabled={isSaving || !canUpdate}
            loading={isSaving}
            intent={B.Intent.PRIMARY}
            small
          >
            Save
          </B.Button>
        </B.Tooltip>
      </div>
    </div>
  );
};
